import _ from "lodash";
import {
  AccountData,
  AccountServiceErrorCode,
  CreateAccountData,
  isFailure,
} from "@sportal/api";

import {
  Notificator,
  NotificationActionTypes,
  NotificationContext,
} from "../../components/notification";
import {
  ACTIVATE_SERVICES_FAILURE,
  CREATE_ACCOUNT_FAILURE,
  GET_ACCOUNT_SUCCESS,
  LOAD_ACCOUNT,
  GET_SPSON,
  GET_SPSON_FAILURE,
  GET_SPSON_SUCCESS,
  UPDATE_WEB_FILTER_FAILURE,
  UPDATE_WEB_FILTER_SUCCESS,
} from "./account.types";
import { AccountActionTypes } from "./account.types";
import { getDefaultTimezone, getSubscriberId } from "../info/info.selectors";
import { SBThunkAction } from "../redux.types";
import { getActivationCommand } from "./account.selectors";
import { getShowErrorPageBeforeAutoLogout } from "../config.selectors";

export const getAccount =
  (): SBThunkAction<void, AccountActionTypes> =>
  async (dispatch, getState, { api }) => {
    const state = getState();
    const id = getSubscriberId(state);
    const defaultTimezone = getDefaultTimezone(state);

    dispatch(loadAccount());

    const result = await api.ssm.account.get(id, defaultTimezone);

    if (isFailure(result)) {
      dispatch(getAccountFailure(result.error));

      return Promise.reject(result.error);
    } else {
      dispatch(getAccountSuccess(result.data));
    }
  };

export const getAccountFailure =
  (
    error: object & { code: number | string }
  ): SBThunkAction<void, AccountActionTypes> =>
  (dispatch, getState) => {
    const showErrorPage = getShowErrorPageBeforeAutoLogout(getState());

    dispatch({
      type: CREATE_ACCOUNT_FAILURE,
      payload: error,
    });

    const notificationMessage =
      error.code !== AccountServiceErrorCode.CANNOT_CREATE_ACCOUNT
        ? "login_error"
        : !showErrorPage
        ? "failed_to_create_account"
        : "";

    if (notificationMessage) {
      dispatch(
        Notificator.error(notificationMessage, {
          context: NotificationContext.System,
        })
      );
    }
  };

export const activateServices =
  (
    isSingleProfile: boolean
  ): SBThunkAction<void, AccountActionTypes | NotificationActionTypes> =>
  async (dispatch, getState, { api }) => {
    const state = getState();
    const id = getSubscriberId(state);
    const command = getActivationCommand(state, isSingleProfile);

    const result = await api.ssm.account.activate(id, command);

    if (isFailure(result)) {
      dispatch(
        Notificator.error("couldnt_activate_services", {
          context: NotificationContext.System,
        })
      );
      dispatch(activateServicesFailure(result.error));

      return Promise.reject(result.error);
    } else {
      dispatch(getAccountSuccess(result.data));
    }
  };

export const updateWebFilter =
  (
    webFilter: boolean
  ): SBThunkAction<void, AccountActionTypes | NotificationActionTypes> =>
  async (dispatch, getState, { api }) => {
    const subscriberId = getSubscriberId(getState());

    const result = await api.ssm.account.toggleSB(subscriberId, webFilter);

    if (isFailure(result)) {
      dispatch(Notificator.error("couldnt_save_changes"));
      dispatch(updateWebFilterFailure(result.error));
    } else {
      dispatch(updateWebFilterSuccess(webFilter));
    }
  };

const loadAccount = (): AccountActionTypes => ({
  type: LOAD_ACCOUNT,
});

export const getSpson =
  () =>
  async (dispatch, getState, { api }) => {
    const { subscriberInfo } = getState();
    dispatch({ type: GET_SPSON });

    const response = await api.ssm.account.getSpson(subscriberInfo.id);

    if (isFailure(response)) {
      if (
        response.error.code === AccountServiceErrorCode.SPSON_IS_NOT_ACTIVATED
      ) {
        return dispatch({ type: GET_SPSON_FAILURE });
      }

      dispatch(Notificator.error("error_failed_to_load_data"));
      return dispatch({ type: GET_SPSON_FAILURE });
    }

    return dispatch({ type: GET_SPSON_SUCCESS });
  };

export const getAccountSuccess = (
  data: AccountData | CreateAccountData
): AccountActionTypes => ({
  type: GET_ACCOUNT_SUCCESS,
  payload: _.omit(data, "time-zone"),
});

export const activateServicesFailure = (
  error: object & { code: number | string }
): AccountActionTypes => ({
  type: ACTIVATE_SERVICES_FAILURE,
  payload: error,
});

export const updateWebFilterSuccess = (
  status: boolean
): AccountActionTypes => ({
  type: UPDATE_WEB_FILTER_SUCCESS,
  payload: status,
});

export const updateWebFilterFailure = (
  error: object & { code: number | string }
): AccountActionTypes => ({
  type: UPDATE_WEB_FILTER_FAILURE,
  payload: error,
});
